import { useEffect } from 'react';
import { Link } from 'react-router';
import { Logo } from '../ui/Logo';
import { useSiteTheme } from '../lib/palette';

const UPDATED = 'October 28, 2026';

/**
 * The public Terms of Service (#567), at /terms for anyone. It must match how billing actually works (#540–#555):
 * trials, renewals, upgrades charged now and downgrades credited, add-ons, pausing, failed payments, cancellation
 * and refunds.
 */
export function TermsOfService() {
  useEffect(() => window.scrollTo(0, 0), []);
  useSiteTheme(); // the public site's look (#719)
  return (
    <div className="legal-page">
      <header className="legal-head">
        <Link to="/" aria-label="ClassGuardian home">
          <Logo />
        </Link>
      </header>
      <main className="legal-body">
        <h1>Terms of Service</h1>
        <p className="muted">Last updated {UPDATED}</p>

        <p>
          These terms are an agreement between you and Vieira Ventures LLC (“we”, “us”), which runs ClassGuardian: the website and web app, the desktop app for
          Windows and Mac, and the browser extension. By creating an account or using ClassGuardian, you agree to them. Our{' '}
          <Link to="/privacy">Privacy Policy</Link> explains what information we collect and how we use it.
        </p>

        <h2>1. Your account</h2>
        <ul>
          <li>You must be at least 18 and the parent or legal guardian of the children you add, or have their parent’s permission to help supervise them.</li>
          <li>Keep your password safe. You’re responsible for what happens in your account, including what adults you invite to your family can do.</li>
          <li>The person who creates a family is its owner. The owner manages the plan, billing and who else has access.</li>
        </ul>

        <h2>2. Using ClassGuardian responsibly</h2>
        <p>ClassGuardian is for parents to run their homeschool and supervise their own children’s school time. You agree to:</p>
        <ul>
          <li>Only supervise children you’re the parent or guardian of, or are allowed to supervise by their parent.</li>
          <li>Not use ClassGuardian to secretly monitor another adult, an employee, or anyone without the legal right to do so.</li>
          <li>
            Tell your children that their school computer is supervised. ClassGuardian shows them when a parent is watching or talking, unless you
            turn on Hidden mode or Ghost mode; then telling them is up to you. Ghost mode is for a parent watching their own minor child’s school screen — the camera and microphone always show the banner.
          </li>
          <li>Not try to break, overload, reverse-engineer, or get around the security of the service, or use it to break the law.</li>
          <li>Only add work samples, documents and test reports about children in your care, and that you have the right to keep and share.</li>
        </ul>

        <h2>3. Plans and free trials</h2>
        <ul>
          <li>
            ClassGuardian offers plans with different features and numbers of students. What each plan includes and costs is shown in the app before you
            subscribe.
          </li>
          <li>
            A plan may start with a <strong>free trial</strong>. You don’t need a card to start one. When the trial ends, subscribe to keep using the
            plan’s features.
          </li>
          <li>
            During <strong>early access</strong>, some or all of ClassGuardian may be free. We’ll tell you well before anything you use starts to cost
            money.
          </li>
        </ul>

        <h2>4. Subscriptions and payment</h2>
        <ul>
          <li>
            Subscriptions are billed <strong>monthly or yearly, in advance</strong>, in US dollars, through our payment processor, Stripe. Sales tax is
            added where it applies.
          </li>
          <li>
            <strong>Your subscription renews automatically</strong> at the end of each billing period, at the price then in effect, until you cancel. You
            authorize us, through Stripe, to charge your payment method for each renewal.
          </li>
          <li>
            <strong>Upgrading</strong> (a bigger plan, more students, an add-on, or switching from monthly to yearly) takes effect right away, and you’re
            charged the difference for the rest of the current period. <strong>Downgrading</strong> or removing an add-on also takes effect right away, and
            the unused part becomes a credit on your next invoice. A smaller plan can’t be chosen while your family uses more than it includes.
          </li>
          <li>
            <strong>Yearly plans:</strong> we’ll email you before a yearly subscription renews, with the date and the amount, so you can cancel first if you
            want to.
          </li>
          <li>
            <strong>Price changes:</strong> we’ll tell you at least 30 days before a new price applies to your subscription. You can cancel before then.
          </li>
          <li>
            <strong>Credits</strong>, such as a free month from a referral, come off future invoices. They have no cash value, can’t be transferred, and
            end if the account is deleted.
          </li>
        </ul>

        <h2>5. Failed payments</h2>
        <p>
          If a payment fails, we’ll retry it and let you know. Your plan keeps working during a short grace period. If it still isn’t paid after that, your
          family’s account is <strong>locked</strong> until you update your payment method: you can still sign in, pay, download your data or delete your
          account, but the rest of ClassGuardian is unavailable and your children’s computers are not supervised. Your records are kept.
        </p>

        <h2>6. Pausing</h2>
        <p>
          When it’s offered, the family owner can pause billing for a set number of months (for example, over the summer). You aren’t charged while
          paused, your records stay available, and your children’s computers are not supervised. Billing resumes automatically at the end of the pause,
          or earlier if you choose.
        </p>

        <h2>7. Cancelling</h2>
        <p>
          You can cancel any time in Settings → Plan &amp; billing → Manage billing. Cancelling stops future renewals; your plan keeps working until the end
          of the period you’ve paid for. After that the account is locked as described above. Your records are kept for 45 days after that, so you can download them (Settings → Account
          security → Download my records) or subscribe again; then they’re deleted. We email you a week before.
        </p>

        <h2>8. Refunds</h2>
        <p>
          If you’re not happy, ask for a refund within <strong>14 days of a charge</strong> and we’ll refund that charge in full. After 14 days, payments
          aren’t refundable and we don’t give partial refunds for unused time, except where the law requires it. Contact us using the details below.
        </p>

        <h2>9. Your content and data</h2>
        <ul>
          <li>
            The records and information you put into ClassGuardian are yours. You give us permission to store and process them only to provide the service
            to you, as described in the Privacy Policy.
          </li>
          <li>You can download your data at any time (Settings → Account security → Export my data) and delete your account and its data.</li>
          <li>You’re responsible for the content you add, and for keeping any records your state requires; ClassGuardian helps but doesn’t replace that.</li>
          <li>
            You decide what to share through an evaluator link, and with whom. Anyone who has a link can see what it shows until it expires or you turn
            it off, so send it only to the person it’s for.
          </li>
        </ul>

        <h2>10. The service</h2>
        <ul>
          <li>
            We work to keep ClassGuardian available and secure, but we can’t promise it will always be uninterrupted or error-free, or that it will catch
            every website or app. Supervision depends on the student’s computer being on, connected, and running the ClassGuardian app or extension.
          </li>
          <li>We may change or improve features. If we remove something important from a plan you pay for, we’ll tell you in advance.</li>
          <li>
            ClassGuardian is a supervision and record-keeping tool. It isn’t a substitute for a parent’s judgment, and it doesn’t give legal or educational
            advice.
          </li>
          <li>
            <strong>State requirements, deadlines and documents</strong> are a starting point we keep from public sources, and laws change. You’re
            responsible for checking your state’s current rules and for what you file, and when. Documents ClassGuardian fills in come from what you
            entered: read them before you sign or send them. Reminders depend on email and notifications reaching you; a missed reminder doesn’t change a
            deadline.
          </li>
        </ul>

        <h2>11. Disclaimers and limits</h2>
        <p>
          To the extent the law allows, ClassGuardian is provided “as is”, without warranties of any kind, and we aren’t liable for indirect, incidental,
          special, consequential or punitive damages, or for lost data, profits or school records. Our total liability for any claim about ClassGuardian is
          limited to the amount you paid us in the 12 months before the claim. Some places don’t allow these limits, so they may not all apply to you.
        </p>

        <h2>12. Your responsibility for how you use ClassGuardian</h2>
        <p>
          You agree to defend and indemnify Vieira Ventures LLC and its owners, employees and contractors, and hold them harmless, against claims brought by
          someone else, and the damages, losses and reasonable costs (including attorneys’ fees) that come from them, when the claim arises from:
        </p>
        <ul>
          <li>
            supervising a computer, person or account you don’t have the right to supervise, such as another adult, a child who isn’t yours or in your
            care, or a computer someone else also uses;
          </li>
          <li>not telling a child that their school computer is supervised, including when you use Hidden mode;</li>
          <li>content you or your children add to ClassGuardian, such as work samples, photos or messages, that you don’t have the right to share; or</li>
          <li>your breaking these terms or the law, including laws about monitoring, recording, or children’s privacy.</li>
        </ul>
        <p>
          We’ll tell you promptly about any such claim and let you take part in defending it. This doesn’t cover claims caused by our own breach of these
          terms or our own negligence, and it applies only as far as the law allows.
        </p>

        <h2>13. Ending your use</h2>
        <p>
          You can stop using ClassGuardian and delete your account at any time. We may suspend or close an account that breaks these terms or puts others
          at risk; if we close a paid account for reasons other than a breach, we’ll refund the unused part of your current period.
        </p>

        <h2>14. Changes to these terms</h2>
        <p>
          We may update these terms. We’ll change the date at the top and, for significant changes, tell you by email or in the app before they take
          effect. Using ClassGuardian after that means you accept the updated terms.
        </p>

        <h2>15. Your state’s rights</h2>
        <p>
          Families use ClassGuardian in every state. Nothing in these terms takes away a consumer protection that your own state’s law gives you. If your
          state requires a notice, a way to cancel, or a refund beyond what these terms describe (for example, a reminder before an automatic renewal),
          we’ll honor it. Where a part of these terms conflicts with a right your state guarantees, that right wins.
        </p>

        <h2>16. The law that applies</h2>
        <p>
          These terms are governed by the laws of the State of South Carolina and the United States, without regard to conflict-of-law rules, and subject to
          section 15. If a part of these terms can’t be enforced, the rest still applies.
        </p>

        <h2>17. Contact</h2>
        <p>
          Email <a href="mailto:support@classguardian.app">support@classguardian.app</a>, call or text (803) 887-0515, or use{' '}
          <strong>Help &amp; Feedback</strong> in the app.
        </p>
      </main>
      <footer className="landing-footer muted small">
        <Logo /> <span>Homeschool, organized. School time, focused.</span>
        <span className="landing-footer-links">
          <Link to="/privacy" className="landing-footer-link">
            Privacy Policy
          </Link>
        </span>
      </footer>
    </div>
  );
}
